
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  SUPPORT_USER = 'SUPPORT_USER',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  /** Admin commercial — accès complet à l'activité commerciale (leads, tâches, opportunités,
   *  RDV, templates email, réassignation) mais aucun accès aux paramètres admin ni au CRUD users. */
  TEAM_MANAGER = 'TEAM_MANAGER',
  MANAGER = 'MANAGER',
  USER = 'USER'
}

export enum SubscriptionPlan {
  BASIC = 'BASIC',
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE'
}

export enum AppointmentType {
  PHONE = 'PHONE',
  VIDEO = 'VIDEO',
  PHYSICAL = 'PHYSICAL'
}

// TYPES POUR LA TIMELINE
export enum LeadActivityType {
  CREATED = 'CREATED',
  STATUS_CHANGED = 'STATUS_CHANGED',
  ASSIGNED = 'ASSIGNED',
  COMMENT_ADDED = 'COMMENT_ADDED',
  COMMENT_DELETED = 'COMMENT_DELETED',
  INFO_UPDATED = 'INFO_UPDATED',
  APPOINTMENT_CREATED = 'APPOINTMENT_CREATED',
  EMAIL_SENT = 'EMAIL_SENT',
  CALL_LOGGED = 'CALL_LOGGED',
  FAVORITE_TOGGLED = 'FAVORITE_TOGGLED'
}

export interface LeadActivity {
  id: string;
  leadId: string;
  userId: string;
  userName: string;
  type: LeadActivityType;
  description: string;
  metadata?: {
    oldValue?: string;
    newValue?: string;
    [key: string]: any;
  };
  createdAt: string;
}

// NOUVEAUX TYPES POUR LES NOTIFICATIONS
export enum NotificationType {
  LEAD_ASSIGNED = 'LEAD_ASSIGNED',
  APPOINTMENT_REMINDER = 'APPOINTMENT_REMINDER',
  LEAD_INACTIVE = 'LEAD_INACTIVE',
  NEW_COMMENT = 'NEW_COMMENT',
  STATUS_CHANGED = 'STATUS_CHANGED',
  MENTION = 'MENTION',
  APPOINTMENT_CREATED = 'APPOINTMENT_CREATED',
  TASK_DUE_SOON = 'TASK_DUE_SOON',
  GDPR_OPPOSITION_RECEIVED = 'gdpr_opposition_received',
  GDPR_OPPOSITION_SLA = 'gdpr_opposition_sla'
}

export interface Notification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  message: string;
  leadId?: string;
  leadName?: string;
  isRead: boolean;
  createdAt: string;
  metadata?: {
    [key: string]: any;
  };
}

export interface PlanConfig {
  id: string;
  label: string;
  priceMonthly: number;
  priceYearly: number;
  userLimit: number;
  features?: string[];
}

export interface CurrencyConfig {
  code: string;
  name: string;
  symbol: string;
}

export interface SystemConfig {
  plans: PlanConfig[];
  currencies: CurrencyConfig[];
  activitySectors?: string[];
}

export interface Company {
  id: string;
  name: string;
  plan: string;
  userLimit: number;
  activeLeads: number;
  status: 'active' | 'suspended';
  createdAt: string;
  address?: string;
  rcs?: string;
  siret?: string;
  phone?: string;
  email?: string;
  website?: string;
  currency?: string;
  timezone?: string;
  theme?: 'light' | 'dark';
  revenueBrackets?: string[];
  revenueColors?: Record<string, string>;
  cardBgColor?: string;
  cardNameColor?: string;
  cardEmailColor?: string;
  cardBgColorLight?: string;
  cardNameColorLight?: string;
  cardEmailColorLight?: string;
  maskContacts?: boolean;
  customStatuses?: StatusDefinition[];
  customFieldDefinitions?: CustomFieldDefinition[];
  inactivityTimeout?: number; // minutes, défaut 10
  userFeatures?: UserFeaturesConfig;
  cardConfig?: CardConfig;
  /** Mode du formulaire de création prospect : 'short' (rapide) ou 'full' (fiche complète) */
  leadCreationMode?: 'short' | 'full';
}

/** Un champ de la carte prospect : clé, visibilité, ordre défini par sa position dans le tableau */
export interface CardFieldDef {
  key: 'name' | 'email' | 'phone' | 'clientCompanyName' | 'status' | 'potentialRevenue' | 'sectorActivity' | 'tag' | 'createdAt' | 'agent';
  enabled: boolean;
  row?: number;  // position dans la grille (0-based), undefined = auto
  col?: number;  // colonne dans la grille (0-based), undefined = auto
  align?: 'left' | 'center' | 'right';  // alignement horizontal du contenu dans la cellule
}

/** Configuration de l'affichage des cartes prospect */
export interface CardConfig {
  fields: CardFieldDef[];
  showQuickCall: boolean;
  gridCols?: number;  // nombre de colonnes : 2 ou 3 (défaut 2)
}

/** Fonctionnalités activables/désactivables pour les utilisateurs (rôle USER) */
export interface UserFeaturesConfig {
  /** Changer de vue (liste / cartes / kanban) */
  viewSwitch?: boolean;
  /** Barre de recherche par mot-clé */
  search?: boolean;
  /** Filtres (agent, statut, secteur) */
  filters?: boolean;
  /** Filtre par couleur de revenu */
  colorFilter?: boolean;
  /** Recherche par tag */
  tagSearch?: boolean;
  /** Export CSV des prospects */
  exportCsv?: boolean;
  /** Import CSV des prospects */
  importCsv?: boolean;
  /** Personnalisation du dashboard (drag, resize, masquer widgets) */
  dashboardCustomize?: boolean;
}

export interface StatusDefinition {
  id: string;
  label: string;
  color: string;       // couleur hex pour charts/badges (#6366f1)
  dotClass: string;     // classe Tailwind pour le dot kanban (bg-indigo-500)
  badgeClass: string;   // classes Tailwind pour le badge liste (bg-indigo-50 text-indigo-700 border-indigo-100)
  isWon?: boolean;
  isLost?: boolean;
}

export type CustomFieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox';

export interface CustomFieldDefinition {
  id: string;
  name: string;
  type: CustomFieldType;
  required?: boolean;
  options?: string[]; // pour type 'select'
}

export interface User {
  id: string;
  companyId: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  managerId?: string;
  inactivityTimeout?: number; // minutes, null = utilise le défaut de la compagnie
  userFeatures?: UserFeaturesConfig; // overrides individuels, undefined = hérite des défauts company
  /** ID d'un profil de rôle custom (table custom_roles) — facultatif, complète le rôle de base. */
  customRoleId?: string | null;
  leadSheetTemplateId?: string | null;
  /** 2FA TOTP activé sur ce compte (lecture seule depuis l'API) */
  totpEnabled?: boolean;
  /** Date d'enrôlement 2FA, ISO 8601 */
  totpEnrolledAt?: string | null;
  /** Préférence : recevoir un email quand un message chat arrive hors-ligne */
  notifyChatEmail?: boolean;
  /** Compte actif. `false` = archivé (typiquement après anonymisation RGPD).
   *  Les comptes archivés sont masqués de la liste par défaut. */
  isActive?: boolean;
}

/** Méthode 2FA choisie par l'utilisateur. */
export type TotpMethod = 'app' | 'email';

// ============================================================================
// Chat interne multi-tenant
// ============================================================================

export type ChatConversationType = 'direct' | 'group';
export type ChatParticipantRole = 'member' | 'observer';

export interface ChatParticipantSummary {
  user_id: string;
  user_name: string;
  role: ChatParticipantRole;
  online?: boolean;   // Phase 3 R2 — last_seen < 2 min sur active_sessions
}

export interface ChatLastMessage {
  id: string;
  sender_id: string;
  content: string;          // tronqué à 200 chars dans la liste
  created_at: string;
}

export interface ChatConversation {
  id: string;
  type: ChatConversationType;
  name: string | null;
  is_private: boolean;
  created_by: string;
  created_at: string;
  last_message_at: string | null;
  my_role: ChatParticipantRole;
  participants: ChatParticipantSummary[];
  last_message: ChatLastMessage | null;
  unread_count: number;
}

/** Phase 3 R2 — événement reçu côté frontend depuis useChatStream. */
export interface ChatPresenceUser {
  user_id: string;
  user_name: string;
}

export interface ChatReactionSummary {
  emoji: string;
  count: number;
  users: string[];   // ids des users qui ont réagi
  me: boolean;       // l'user courant a-t-il réagi avec cet emoji ?
}

export interface ChatAttachment {
  id: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
}

export interface ChatMessage {
  id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  reactions?: ChatReactionSummary[];
  attachments?: ChatAttachment[];
}

export interface ChatSearchResult {
  id: string;                  // message id
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  content: string;
  created_at: string;
  conv_type: ChatConversationType;
  conv_name: string | null;
  relevance: number;
}

/** Réponse de l'endpoint /login : soit l'user complet, soit une demande de 2FA. */
export type LoginResult =
  | { kind: 'success'; user: User }
  | { kind: 'totp_required'; pendingToken: string; totpMethod: TotpMethod };

/** Configuration d'affichage d'une fiche prospect (modale de détail) */
export interface LeadSheetConfig {
  tabs?: {
    comments?: boolean;
    email?: boolean;
    timeline?: boolean;       // historique / activité
    opportunities?: boolean;
    tasks?: boolean;
    files?: boolean;
    erp?: boolean;
  };
  actions?: {
    call?: boolean;           // bouton Appeler
    logCall?: boolean;        // bouton Enregistrer un appel
    favorite?: boolean;       // étoile favori
    appointment?: boolean;    // création de rendez-vous
  };
  fields?: {
    clientCompanyName?: boolean;
    email?: boolean;
    phone?: boolean;
    phoneLandline?: boolean;
    tag?: boolean;
    sectorActivity?: boolean;
    assignedTo?: boolean;
    potentialRevenue?: boolean;
    additionalInfo?: boolean;   // notes internes
    appointmentsList?: boolean; // liste rdv (panneau latéral)
    /** Toggle individuel par champ personnalisé : clé = `custom:${customFieldId}`. */
    [customKey: string]: boolean | undefined;
  };
  /** Ordre d'affichage des champs du panneau latéral (hors carte Contact).
   *  Valeurs possibles : 'tag', 'sectorActivity', 'assignedTo', 'potentialRevenue',
   *  'additionalInfo', 'appointmentsList', ou `custom:<customFieldId>`.
   *  Les champs non listés sont rendus à la fin dans leur ordre par défaut. */
  fieldOrder?: string[];
  /** Ordre d'affichage des onglets du panneau droit.
   *  Valeurs possibles : 'comments', 'email', 'timeline', 'opportunities', 'tasks', 'files', 'erp'. */
  tabOrder?: string[];
}

export interface LeadSheetTemplate {
  id: string;
  companyId: string;
  name: string;
  isDefault: boolean;
  config: LeadSheetConfig;
  createdAt?: string;
  updatedAt?: string;
}

export interface Lead {
  id: string;
  companyId: string;
  clientCompanyName?: string;
  tag?: string;
  sectorActivity?: string;
  name: string;
  email: string;
  phone: string;
  phoneLandline?: string;
  status: string;
  assignedTo: string;
  createdAt: string;
  additionalInfo?: string;
  potentialRevenue?: string;
  isFavorite?: boolean;
  source?: string;
  erpCustomerId?: string;
  customFields?: Record<string, any>;
}

export type ReminderType = 'notification' | 'email';

export interface AppointmentReminder {
  id?: string;
  type: ReminderType;
  minutesBefore: number;
  sentAt?: string | null;
}

export interface Appointment {
  id: string;
  leadId: string;
  userId: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: AppointmentType;
  color?: string | null;
  clientEmail?: string;
  reminders?: AppointmentReminder[];
}

export interface LeadComment {
  id: string;
  leadId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
}

export interface OpportunityItem {
  id: string;
  opportunityId: string;
  label: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface Opportunity {
  id: string;
  companyId: string;
  leadId?: string;
  clientCompanyName: string;
  subject: string;
  comment?: string;
  totalAmount: number;
  createdBy: string;
  createdAt: string;
  items: OpportunityItem[];
}

export type TaskStatus = 'todo' | 'done';

export interface Task {
  id: string;
  companyId: string;
  leadId?: string;
  clientCompanyName?: string;
  title: string;
  comment?: string;
  dueAt: string;
  status: TaskStatus;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
}

/** Règle d'auto-affectation des prospects. Évaluée dans l'ordre `position` ASC. */
export interface AssignmentRule {
  id: string;
  position: number;
  name: string;
  enabled: boolean;
  conditions: {
    sector?: string;
    tag?: string;
    revenueBrackets?: string[];
  };
  actionType: 'user' | 'round_robin';
  actionUserId?: string | null;
  actionPool?: string[];
  lastAssignedUserId?: string | null;
}

export interface CallStat {
  userId: string;
  callCount: number;
  avgDuration: number;
  totalDuration: number;
  period: 'day' | 'week' | 'month';
}

/** Métadonnées embarquées dans le manifest.json de chaque backup. */
export interface BackupManifest {
  version: string;
  created_at: string;
  created_by?: { uid: string; name?: string; email?: string };
  type?: 'manual' | 'safety_pre_restore';
  restore_of?: string;
  db_name?: string;
  db_host?: string;
  tables_count?: number;
  tables?: string[];
  migrations?: string[];
  dump_size_bytes?: number;
  uploads_size_bytes?: number;
  uploads_files?: number;
}

/** Un backup complet du CRM (BDD + uploads + manifest). */
export interface Backup {
  filename: string;
  size_bytes: number;
  created_at: string;
  manifest: BackupManifest | null;
}
